/**
 * EditModal Component
 *
 * Modal overlay for editing text and images in the inline editor.
 * Shows different UI based on element type (text vs image).
 * Uses a portal to render at the document body level so it's not
 * constrained by parent overflow containers.
 */

import { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useRenderer } from '../context';
import { TextEditor } from './TextEditor';
import { ImageEditor } from './ImageEditor';

// ============================================================================
// EditModal Component
// ============================================================================

export function EditModal() {
  const { selectedElement, selectElement, websiteId } = useRenderer();

  const handleClose = useCallback(() => {
    selectElement(null);
  }, [selectElement]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (selectedElement) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [selectedElement, handleClose]);

  // Don't render if no element is selected
  if (!selectedElement) {
    return null;
  }

  const isImage = selectedElement.type === 'image';

  // Use portal to render at document body level
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />

      {/* Modal - positioned relative to the fixed container */}
      <div className="relative z-10 max-h-[90vh] overflow-y-auto">
        <div
          className={`relative bg-white rounded-xl shadow-2xl w-full transform transition-all ${
            isImage ? 'max-w-4xl' : 'max-w-2xl'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {isImage ? 'Edit Image' : 'Edit Text'}
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {selectedElement.label}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="p-6">
            {isImage ? (
              <ImageEditor
                element={selectedElement}
                websiteId={websiteId}
                onClose={handleClose}
              />
            ) : (
              <TextEditor
                element={selectedElement}
                websiteId={websiteId}
                onClose={handleClose}
              />
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
